// Stores and stock (docs/plans/phase1-stock.md §3.3, §5). Every write carries a client
// request key so a retried request can't post stock twice.
import { queryOptions } from '@tanstack/react-query';
import {
  adjustmentSchema,
  countSchema,
  countSummarySchema,
  expiringRowSchema,
  fefoPreviewSchema,
  grnSchema,
  grnSummarySchema,
  pageSchema,
  setLevelsBodySchema,
  stockCardRowSchema,
  stockItemSchema,
  stockOnHandRowSchema,
  storeItemDetailSchema,
  storeSchema,
  supplierSchema,
  transferSchema,
  type approveCountBodySchema,
  type countLineBodySchema,
  type createAdjustmentBodySchema,
  type createGrnBodySchema,
  type createStoreBodySchema,
  type createSupplierBodySchema,
  type createTransferBodySchema,
  type setStockDetailsBodySchema,
  type updateGrnBodySchema,
  type updateStoreBodySchema,
  type updateSupplierBodySchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

type In<T extends z.ZodType> = z.input<T>;

// --- Stores and suppliers -------------------------------------------------------------------

export const storesQuery = queryOptions({
  queryKey: ['stores'],
  queryFn: () => api('GET', '/stores', { schema: z.array(storeSchema) }),
});

export const adminStoresQuery = queryOptions({
  queryKey: ['admin', 'stores'],
  queryFn: () => api('GET', '/admin/stores', { schema: z.array(storeSchema) }),
});

export const createStore = (body: In<typeof createStoreBodySchema>) =>
  api('POST', '/admin/stores', { body, schema: storeSchema });

export const updateStore = (id: string, body: In<typeof updateStoreBodySchema>) =>
  api('PATCH', `/admin/stores/${id}`, { body, schema: storeSchema });

export const searchSuppliers = (query: string, includeInactive = false) =>
  api('POST', '/suppliers/search', {
    body: { query, includeInactive, limit: 50 },
    schema: z.array(supplierSchema),
  });

export const createSupplier = (body: In<typeof createSupplierBodySchema>) =>
  api('POST', '/suppliers', { body, schema: supplierSchema });

export const updateSupplier = (id: string, body: In<typeof updateSupplierBodySchema>) =>
  api('PATCH', `/suppliers/${id}`, { body, schema: supplierSchema });

// --- Items and balances ---------------------------------------------------------------------

export const searchStockItems = (query: string) =>
  api('POST', '/stock-items/search', {
    body: { query, limit: 20 },
    schema: z.array(stockItemSchema),
  });

export const setStockDetails = (itemId: string, body: In<typeof setStockDetailsBodySchema>) =>
  api('PUT', `/stock-items/${itemId}`, { body, schema: stockItemSchema });

export const stockOnHandQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'on-hand'],
    queryFn: () =>
      api('GET', `/stores/${storeId}/stock?limit=200`, {
        schema: pageSchema(stockOnHandRowSchema),
      }),
  });

export const storeItemQuery = (storeId: string, itemId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'item', itemId],
    queryFn: () =>
      api('GET', `/stores/${storeId}/items/${itemId}`, { schema: storeItemDetailSchema }),
  });

export const stockCardQuery = (storeId: string, itemId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'card', itemId],
    queryFn: () =>
      api('GET', `/stores/${storeId}/items/${itemId}/movements?limit=200`, {
        schema: pageSchema(stockCardRowSchema),
      }),
  });

export const setLevels = (storeId: string, itemId: string, body: In<typeof setLevelsBodySchema>) =>
  api('PUT', `/stores/${storeId}/items/${itemId}/levels`, { body, schema: setLevelsBodySchema });

export const expiringQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'expiring'],
    queryFn: () =>
      api('GET', `/stores/${storeId}/expiring`, { schema: z.array(expiringRowSchema) }),
  });

export const reorderQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'reorder'],
    queryFn: () =>
      api('GET', `/stores/${storeId}/reorder`, { schema: z.array(stockOnHandRowSchema) }),
  });

export const fefoPreview = (storeId: string, itemId: string, quantity: number) =>
  api('POST', `/stores/${storeId}/fefo-preview`, {
    body: { itemId, quantity },
    schema: fefoPreviewSchema,
  });

// --- Documents ----------------------------------------------------------------------------

export const draftGrnsQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'grns', 'draft'],
    queryFn: () =>
      api('GET', `/goods-receipts?storeId=${storeId}&status=draft`, {
        schema: pageSchema(grnSummarySchema),
      }),
  });

export const grnQuery = (id: string) =>
  queryOptions({
    queryKey: ['grn', id],
    queryFn: () => api('GET', `/goods-receipts/${id}`, { schema: grnSchema }),
  });

export const createGrn = (body: In<typeof createGrnBodySchema>) =>
  api('POST', '/goods-receipts', { body, schema: grnSchema });

export const updateGrn = (id: string, body: In<typeof updateGrnBodySchema>) =>
  api('PATCH', `/goods-receipts/${id}`, { body, schema: grnSchema });

export const postGrn = (
  id: string,
  expectedVersion: number,
  shortExpiryReasons: Record<string, string>,
  overReceiptReasons: Record<string, string> = {},
) =>
  api('POST', `/goods-receipts/${id}/post`, {
    body: { expectedVersion, shortExpiryReasons, overReceiptReasons },
    schema: grnSchema,
  });

export const createTransfer = (body: In<typeof createTransferBodySchema>) =>
  api('POST', '/stock-transfers', { body, schema: transferSchema });

export const adjustmentsQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'adjustments'],
    queryFn: () =>
      api('GET', `/stock-adjustments?storeId=${storeId}&limit=100`, {
        schema: z.array(adjustmentSchema),
      }),
  });

export const createAdjustment = (body: In<typeof createAdjustmentBodySchema>) =>
  api('POST', '/stock-adjustments', { body, schema: adjustmentSchema });

export const decideAdjustment = (
  id: string,
  decision: 'approve' | 'reject' | 'cancel',
  reason: string,
) =>
  api('POST', `/stock-adjustments/${id}/${decision}`, {
    body: { reason },
    schema: adjustmentSchema,
  });

export const countsQuery = (storeId: string) =>
  queryOptions({
    queryKey: ['stock', storeId, 'counts'],
    queryFn: () =>
      api('GET', `/stock-counts?storeId=${storeId}`, { schema: z.array(countSummarySchema) }),
  });

export const countQuery = (id: string) =>
  queryOptions({
    queryKey: ['count', id],
    queryFn: () => api('GET', `/stock-counts/${id}`, { schema: countSchema }),
  });

export const startCount = (storeId: string, clientRequestId: string) =>
  api('POST', '/stock-counts', { body: { clientRequestId, storeId }, schema: countSchema });

export const saveCountLine = (id: string, body: In<typeof countLineBodySchema>) =>
  api('PUT', `/stock-counts/${id}/lines`, { body, schema: countSchema });

export const submitCount = (id: string) =>
  api('POST', `/stock-counts/${id}/submit`, { schema: countSchema });

export const approveCount = (id: string, body: In<typeof approveCountBodySchema>) =>
  api('POST', `/stock-counts/${id}/approve`, { body, schema: countSchema });

export const decideCount = (id: string, decision: 'reject' | 'cancel', reason: string) =>
  api('POST', `/stock-counts/${id}/${decision}`, { body: { reason }, schema: countSchema });
